// Composant ComboBox moderne pour les formulaires U2000, avec design system unifié et intégration formStateHandlers
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import U2000Label from "../label/U2000Label";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";
import U2000ComboBoxBase from "./U2000ComboBoxBase";

interface U2000ComboBoxProps<T = any> {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label: string;
  data: T[];
  keyField?: keyof T;
  descField?: keyof T;
  readonly?: boolean;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Hauteur maximale du dropdown en pixels (défaut: 300)
   */
  maxHeight?: number;
  /**
   * Libellé de l’entrée « aucune sélection » (première ligne du menu).
   */
  noneItemLabel?: string;
  /**
   * Function to transform the data item to get the value stored in formStateHandlers
   * If not provided, uses keyField directly
   */
  getValue?: (item: T) => any;
  /**
   * Function to find the data item from the stored value
   * If not provided, uses keyField to match
   */
  findItemByValue?: (value: any, data: T[]) => T | null;
  /**
   * Function to format the label for display
   * If not provided, uses descField directly
   */
  formatLabel?: (item: T) => string;
  /** BL-0388 : champs orgConfig éditables même si le formulaire parent est en lecture (mission système). */
  ignoreFormReadState?: boolean;
  /** Grille compacte U3129 (h-8). */
  compactGrid?: boolean;
}

// Utilitaire pour accéder à une propriété imbriquée d'un objet à partir d'un chemin
function getNestedValue<T extends Record<string, any>>(formStateHandlers: U2000FormStateHandlers<T>, fieldName: string): any {
  const data = formStateHandlers.getData() as Record<string, any>;
  return fieldName.split(".").reduce((acc: any, part: string) => (acc !== undefined && acc !== null ? acc[part] : undefined), data);
}

// Composant principal modernisé avec design cohérent
const U2000ComboBox = <T extends Record<string, any>>({
  formStateHandlers,
  fieldName,
  label,
  data = [],
  keyField = "id" as keyof T,
  descField = "description" as keyof T,
  readonly = false,
  required = false,
  placeholder,
  disabled = false,
  maxHeight = 300,
  noneItemLabel: noneItemLabelProp,
  getValue,
  findItemByValue,
  formatLabel,
  ignoreFormReadState = false,
  compactGrid = false,
}: U2000ComboBoxProps<T>) => {
  const { t } = useTranslation();
  const noneItemLabel = noneItemLabelProp ?? t("Aucune sélection", { ns: "common" });

  // Get current value from formStateHandlers
  const currentValue = getNestedValue(formStateHandlers, fieldName);

  // Find the selected item based on current value
  const selectedItem = useMemo(() => {
    if (currentValue == null || currentValue === "") return null;
    if (findItemByValue) {
      return findItemByValue(currentValue, data);
    }
    return (
      data.find((item) => {
        const itemValue = getValue ? getValue(item) : item[keyField];
        return String(itemValue) === String(currentValue);
      }) || null
    );
  }, [currentValue, data, keyField, findItemByValue, getValue]);

  // Check for errors
  const hasError = (formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName]) || (formStateHandlers.isSubmitted() && formStateHandlers.getErrors()[fieldName]);
  const isDisabled =
    disabled ||
    readonly ||
    (!ignoreFormReadState && formStateHandlers.getFormState() === U2000FormStateEnumState.READ);

  // Handle change
  const handleChange = (key: string | null) => {
    // Marquer touché pour afficher le message d’erreur (validateForm au onChange)
    // sans attendre un blur peu fiable sur sélection dans le dropdown.
    formStateHandlers.markTouched?.(fieldName);

    // Si key est null, c'est l'item "aucun item sélectionné"
    if (key === null) {
      const fakeEvent = { target: { id: fieldName } } as React.ChangeEvent<HTMLInputElement>;
      if (formStateHandlers.onChange) {
        formStateHandlers.onChange(fakeEvent, null);
      }
      return;
    }

    // Find item in formattedData first (which has value/label structure)
    const formattedItem = formattedData.find((item) => String(item.value) === key);
    if (formattedItem && formattedItem.originalItem) {
      const selectedItem = formattedItem.originalItem;
      const valueToStore = getValue ? getValue(selectedItem) : selectedItem[keyField];
      const fakeEvent = { target: { id: fieldName } } as React.ChangeEvent<HTMLInputElement>;
      if (formStateHandlers.onChange) {
        formStateHandlers.onChange(fakeEvent, valueToStore);
      }
    }
  };

  // Handle focus
  const handleFocus = () => {
    // Focus handler for U2000ComboBoxBase
  };

  // Handle blur
  const handleBlur = () => {
    if (formStateHandlers.onBlur) {
      const fakeEvent = { target: { id: fieldName } } as React.FocusEvent<HTMLInputElement>;
      formStateHandlers.onBlur(fakeEvent);
    }
  };

  // Format data for U2000ComboBoxBase
  const formattedData = useMemo(() => {
    return data.map((item) => {
      const itemValue = getValue ? getValue(item) : item[keyField];
      const key = String(itemValue);
      const label = formatLabel ? formatLabel(item) : String(item[descField]);
      return {
        value: key,
        label: label,
        originalItem: item,
      };
    });
  }, [data, keyField, descField, formatLabel, getValue]);

  // Get value key for selected item (for U2000ComboBoxBase)
  const selectedValueKey = useMemo(() => {
    if (!selectedItem) return null;
    const itemValue = getValue ? getValue(selectedItem) : selectedItem[keyField];
    return String(itemValue);
  }, [selectedItem, keyField, getValue]);

  // Get formatted item for selected value (includes originalItem)
  const selectedFormattedItem = useMemo(() => {
    if (!selectedValueKey) return null;
    return formattedData.find((item) => item.value === selectedValueKey) || null;
  }, [selectedValueKey, formattedData]);

  return (
    <div className="space-y-1.5">
      <U2000Label label={label} required={required} htmlFor={fieldName} />

      <div className={hasError ? "[&>div>button]:border-red-300 [&>div>button:focus]:border-red-500 [&>div>button:focus]:ring-red-500/20" : ""}>
        <U2000ComboBoxBase
          keyField="value"
          descField="label"
          data={formattedData}
          value={selectedFormattedItem}
          placeHolder={placeholder || ""}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={isDisabled}
          maxHeight={maxHeight}
          noneItemLabel={noneItemLabel}
          compactGrid={compactGrid}
        />
      </div>

      {/* Affichage moderne du message d'erreur */}
      <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
};

export default U2000ComboBox;
